import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { FileText, Upload, MessageSquareDashed, File, X } from "lucide-react";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ManualKBCreation } from "@/components/ManualKBCreation";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { extractContentFromFile } from "@/utils/universalContentExtractor";
import teamHandsIcon from "@/assets/team-hands-icon.png";
import gibletOwlWhite from "@/assets/giblet-owl-white.png";
import gibletNoEyes from "@/assets/giblet-no-eyes.png";
import { ChannelSelector } from "@/components/channels/ChannelSelector";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { Badge } from "@/components/ui/badge";

const Creators = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isCompositionEnabled, isConsumptionEnabled } = useFeatureFlags();
  const [createMethod, setCreateMethod] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);
  const [gibletDescription, setGibletDescription] = useState<string>("");
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Refs for eye tracking
  const containerRef = useRef<HTMLDivElement>(null);
  const leftEyeRef = useRef<SVGCircleElement>(null);
  const rightEyeRef = useRef<SVGCircleElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  // Mouse position state for eye tracking
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // Eye socket positions (same as Index.tsx)
  const leftEyeBaseX = 109;
  const leftEyeBaseY = 45;
  const rightEyeBaseX = 189;
  const rightEyeBaseY = 45;
  const eyeSize = 36;
  const pupilSize = 25;
  const maxOffset = 12;
  const movementDivisor = 18;

  // Reset to main view when navigated with reset state
  useEffect(() => {
    if (location.state?.reset) {
      setCreateMethod(null);
      setSelectedFiles([]);
      setGibletDescription("");
      setSelectedChannelId(null);
      // Clear the state to prevent reset on future renders
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  // Fetch available channels
  const { data: channels = [] } = useQuery({
    queryKey: ['channels'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('channels')
        .select('*')
        .order('name');
      if (error) throw error;
      return data;
    }
  });

  // Track mouse movement for eye animation
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // Update eye positions based on mouse
  useEffect(() => {
    if (!svgRef.current || !leftEyeRef.current || !rightEyeRef.current) return;

    const svgRect = svgRef.current.getBoundingClientRect();

    const calculateEyePosition = (eyeBaseX: number, eyeBaseY: number) => {
      const eyeCenterX = svgRect.left + eyeBaseX + eyeSize / 2;
      const eyeCenterY = svgRect.top + eyeBaseY + eyeSize / 2;
      
      const deltaX = mousePosition.x - eyeCenterX;
      const deltaY = mousePosition.y - eyeCenterY;
      const angle = Math.atan2(deltaY, deltaX);
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      
      const movement = Math.min(distance / movementDivisor, maxOffset);
      
      let offsetX = Math.cos(angle) * movement;
      let offsetY = Math.sin(angle) * movement;
      
      // Constrain within circular bounds
      const offsetMagnitude = Math.sqrt(offsetX * offsetX + offsetY * offsetY);
      if (offsetMagnitude > maxOffset) {
        offsetX = (offsetX / offsetMagnitude) * maxOffset;
        offsetY = (offsetY / offsetMagnitude) * maxOffset;
      }
      
      return { x: eyeBaseX + offsetX, y: eyeBaseY + offsetY };
    };

    const leftPos = calculateEyePosition(leftEyeBaseX, leftEyeBaseY);
    const rightPos = calculateEyePosition(rightEyeBaseX, rightEyeBaseY);

    leftEyeRef.current.setAttribute('cx', String(leftPos.x + eyeSize / 2));
    leftEyeRef.current.setAttribute('cy', String(leftPos.y + eyeSize / 2));
    rightEyeRef.current.setAttribute('cx', String(rightPos.x + eyeSize / 2));
    rightEyeRef.current.setAttribute('cy', String(rightPos.y + eyeSize / 2));
  }, [mousePosition]);

  const resetDemo = () => {
    setCreateMethod(null);
    setSelectedFiles([]);
  };

  const handleFileImport = () => {
    toast({
      title: "File import started",
      description: "Your documents are being processed"
    });
    setCreateMethod("import");
  };

  const handleGibletCreate = () => {
    toast({
      title: "Giblet assistance requested",
      description: "Giblet will help you create your knowledge base"
    });
    setCreateMethod("giblet");
  };


  const handleFileSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      setSelectedFiles(prevFiles => [...prevFiles, ...files]);
      toast({
        title: `Selected ${files.length} file(s)`,
        description: files.map(file => file.name).join(", ")
      });
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const openFileDialog = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };


  return <div className="min-h-screen bg-white">
      <Navbar />
      <main className="container mx-auto px-4 flex flex-col gap-8" style={{ minHeight: 'calc(100vh - 64px)' }}>
        
        {/* Top 1/3: Roaming Eyes Giblet - NOT CLICKABLE */}
        <div className="flex-[1] flex items-center justify-center pt-4" ref={containerRef}>
          <motion.div 
            className="relative float-animation" 
            initial={{ scale: 0.8, opacity: 0 }} 
            animate={{ scale: 1, opacity: 1 }}
          >
            <svg ref={svgRef} width="334" height="206" viewBox="0 0 334 206" className="animate-fade-in">
              {/* Base Giblet image with empty eye sockets */}
              <image href={gibletNoEyes} width="334" height="206" x="0" y="0" />
              
              {/* Left pupil */}
              <circle 
                ref={leftEyeRef}
                cx={leftEyeBaseX + eyeSize / 2} 
                cy={leftEyeBaseY + eyeSize / 2} 
                r={pupilSize / 2} 
                fill="black"
                style={{ transition: 'all 0.15s ease-out' }}
              />
              {/* Right pupil */}
              <circle 
                ref={rightEyeRef}
                cx={rightEyeBaseX + eyeSize / 2} 
                cy={rightEyeBaseY + eyeSize / 2} 
                r={pupilSize / 2} 
                fill="black"
                style={{ transition: 'all 0.15s ease-out' }}
              />
            </svg>
          </motion.div>
        </div>

        {/* Bottom 2/3: Existing content */}
        <div className="flex-[2] pb-8 pt-4">
          <div className="text-center mb-6">
            <h1 className="text-4xl font-sans font-bold text-black">The Wisdom of the Workforce</h1>
          </div>

          <div className="mx-auto max-w-2xl mb-12">
            <Card className="border-0">
              <CardContent className="pt-6">
                {!createMethod ? <div className="space-y-6">
                        <div className="grid gap-4 md:grid-cols-3">
                          <Popover>
                            <PopoverTrigger asChild>
                              <Card className="border-2 bg-black border-black hover:shadow-md transition-all cursor-pointer">
                                <CardContent className="pt-4 pb-4 flex flex-col items-center text-center">
                                  <h3 className="text-2xl font-sans text-white">Create Knowledge</h3>
                                </CardContent>
                              </Card>
                            </PopoverTrigger>
                            <PopoverContent side="bottom" align="center" sideOffset={8} avoidCollisions={false} className="max-w-sm text-sm p-4">
                              <p>Build and curate knowledge bases as the contextual assets that capture expertise, insights, and domain knowledge from your lived experience. Each asset is attributed directly to its Creator(s), with both usage and commercial rights embedded from the start.</p>
                            </PopoverContent>
                          </Popover>
                          
                          {/* Compose Solutions - Gated by bt2_composition_active */}
                          <Popover>
                            <PopoverTrigger asChild>
                              <Card className={`border-2 border-black transition-all ${isCompositionEnabled ? 'bg-black hover:shadow-md cursor-pointer' : 'bg-gray-400 cursor-not-allowed'}`}>
                                <CardContent className="pt-4 pb-4 flex flex-col items-center text-center relative">
                                  <h3 className="text-2xl font-sans text-white">Compose Solutions</h3>
                                  {!isCompositionEnabled && (
                                    <Badge variant="secondary" className="absolute top-2 right-2 text-[10px] px-1.5 py-0">
                                      Soon
                                    </Badge>
                                  )}
                                </CardContent>
                              </Card>
                            </PopoverTrigger>
                            <PopoverContent side="bottom" align="center" sideOffset={8} avoidCollisions={false} className="max-w-sm text-sm p-4">
                              <p>Assemble knowledge bases into comprehensive solutions that address complex enterprise challenges through structured collaboration and AI-assisted orchestration. The reward for successful Composition is a Solution Bounty posted by the Consumer looking to solve the problem.</p>
                            </PopoverContent>
                          </Popover>
                          
                          {/* Consume Wisdom - Gated by bt3_consumption_active */}
                          <Popover>
                            <PopoverTrigger asChild>
                              <Card className={`border-2 border-black transition-all ${isConsumptionEnabled ? 'bg-black hover:shadow-md cursor-pointer' : 'bg-gray-400 cursor-not-allowed'}`}>
                                <CardContent className="pt-4 pb-4 flex flex-col items-center text-center relative">
                                  <h3 className="text-2xl font-sans text-white">Consume Wisdom</h3>
                                  {!isConsumptionEnabled && (
                                    <Badge variant="secondary" className="absolute top-2 right-2 text-[10px] px-1.5 py-0">
                                      Soon
                                    </Badge>
                                  )}
                                </CardContent>
                              </Card>
                            </PopoverTrigger>
                            <PopoverContent side="bottom" align="center" sideOffset={8} avoidCollisions={false} className="max-w-sm text-sm p-4">
                              <p>Access the marketplace where knowledge bases and solutions can be purchased, licensed, staked for collaboration, or retired. Incentivize Creation of knowledge and solutions by issuing Calls and assigning a bounty.</p>
                            </PopoverContent>
                          </Popover>
                        </div>
                    </div> : createMethod === "import" ? <div className="space-y-6">
                      <h3 className="text-xl font-sans text-center mb-6">Import Content</h3>
                      
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                        {selectedFiles.length === 0 ? <>
                            <Upload className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                            <h4 className="text-lg font-medium mb-2">Drag and drop files here</h4>
                            <p className="text-sm text-gray-500 mb-4">
                              All file types supported: PDF, Word, PowerPoint, Excel, Text, Audio (MP3/WAV), Images, and more
                            </p>
                          </> : <>
                            <h4 className="text-lg font-medium mb-4">Selected Documents ({selectedFiles.length})</h4>
                            <div className="max-h-60 overflow-y-auto mb-4">
                              <ul className="space-y-2">
                                {selectedFiles.map((file, index) => <li key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded border">
                                    <div className="flex items-center">
                                      <File className="h-4 w-4 mr-2 text-gray-600" />
                                      <span className="text-sm truncate max-w-xs">{file.name}</span>
                                    </div>
                                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => removeFile(index)}>
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </li>)}
                              </ul>
                            </div>
                          </>}
                        <div className="flex justify-center">
                          <Button className="mx-auto" onClick={openFileDialog}>
                            Browse Files
                          </Button>
                          <Input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileSelection} />
                        </div>
                      </div>
                      
                      <div className="bg-slate-50 rounded-lg p-4">
                        <h4 className="text-lg font-medium mb-4">Select Channel</h4>
                        <ChannelSelector
                          channels={channels}
                          value={selectedChannelId || ''}
                          onValueChange={setSelectedChannelId}
                          placeholder="Choose a channel for this knowledge base"
                          className="w-full"
                        />
                        <p className="text-sm text-gray-500 mt-2">
                          This determines which domain categories will be available for tagging
                        </p>
                      </div>
                      
                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start">
                        <div className="mr-3 mt-1 text-amber-500">
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="8" x2="12" y2="12"></line>
                            <line x1="12" y1="16" x2="12.01" y2="16"></line>
                          </svg>
                        </div>
                        <div>
                          <h5 className="font-medium text-amber-800">Privacy Notice</h5>
                          <p className="text-sm text-amber-700">
                            Imported documents will be processed securely. You retain all rights to your content.
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex justify-between pt-4">
                  <Button type="button" variant="active" onClick={resetDemo}>
                    Start Over
                  </Button>
                        <Button 
                          onClick={async () => {
                            if (!user) {
                              toast({
                                title: "Authentication Required",
                                description: "Please log in to upload files",
                                variant: "destructive"
                              });
                              navigate('/auth');
                              return;
                            }

                            if (selectedFiles.length === 0) {
                              toast({
                                title: "No Files Selected",
                                description: "Please select at least one file to continue",
                                variant: "destructive"
                              });
                              return;
                            }

                            if (!selectedChannelId) {
                              toast({
                                title: "Channel Required",
                                description: "Please select a channel for this knowledge base",
                                variant: "destructive"
                              });
                              return;
                            }

                            setIsUploading(true);
                            
                            try {
                              // Upload files to Supabase storage
                              const uploadedFileData = await Promise.all(
                                selectedFiles.map(async (file) => {
                                  const fileExt = file.name.split('.').pop();
                                  const fileName = `${user.id}/${Date.now()}-${file.name}`;
                                  
                                  const { data, error } = await supabase.storage
                                    .from('knowledge-bases')
                                    .upload(fileName, file);
                                  
                                  if (error) throw error;
                                  
                                  const { data: { publicUrl } } = supabase.storage
                                    .from('knowledge-bases')
                                    .getPublicUrl(fileName);
                                  
                                  return {
                                    name: file.name,
                                    url: publicUrl,
                                    size: file.size,
                                    type: file.type
                                  };
                                })
                              );

                              toast({
                                title: "Files uploaded",
                                description: "Analyzing content to generate metadata...",
                              });

                              // Extract complete content from the first file (any type)
                              let extractedContent = '';
                              let extractionMethod = 'none';
                              try {
                                const firstFile = selectedFiles[0];
                                const { content, method, pages } = await extractContentFromFile(firstFile);
                                extractedContent = content; // Use FULL content, no truncation
                                extractionMethod = method;
                                
                                console.log(`Extracted ${content.length} chars via ${method}${pages ? ` (${pages} pages)` : ''}`);
                                
                                if (content.trim().length < 50) {
                                  toast({
                                    title: "Warning",
                                    description: `Could not extract enough text from ${firstFile.name}. AI suggestions may be limited.`,
                                  });
                                } else {
                                  toast({
                                    title: "Content Extracted",
                                    description: `Extracted ${content.length} characters from ${firstFile.name} via ${method}`,
                                  });
                                }
                              } catch (readError) {
                                console.error('Failed to extract content:', readError);
                                toast({
                                  title: "Extraction Failed",
                                  description: `Could not read ${selectedFiles[0].name}. Using filename-based metadata.`,
                                  variant: "destructive"
                                });
                                // Continue with filename-based fallback
                              }

                              let title = '';
                              let description = '';

                              // Try to generate AI metadata if we have content
                              if (extractedContent.trim().length > 50) {
                                try {
                                  console.log('🤖 Calling AI to analyze content:', extractedContent.substring(0, 200) + '...');
                                  
                                  const { data: metadata, error: aiError } = await supabase.functions.invoke('generate-kb-metadata', {
                                    body: { 
                                      content: extractedContent,
                                      contentType: 'kb'
                                    }
                                  });

                                  console.log('🤖 AI Response:', metadata, aiError);

                                  if (aiError) {
                                    console.error('❌ AI generation failed:', aiError);
                                    toast({
                                      title: "AI Analysis Failed",
                                      description: "Using filename fallback. Error: " + (aiError.message || 'Unknown error'),
                                      variant: "destructive"
                                    });
                                  } else if (metadata) {
                                    console.log('✅ AI extracted metadata:', { 
                                      title: metadata.title, 
                                      description: metadata.description?.substring(0, 100) 
                                    });
                                    
                                    const selectedChannel = channels.find(c => c.id === selectedChannelId);
                                    
                                    // Navigate to review screen with AI suggestions and channel context
                                    navigate("/knowledge-base-review", {
                                      state: {
                                        extractedContent, // Pass full content for debugging
                                        extractionMethod,
                                        aiSuggestions: {
                                          title: metadata.title || selectedFiles[0].name.replace(/\.[^/.]+$/, ""),
                                          titleSuggestion: metadata.titleSuggestion,
                                          description: metadata.description || "",
                                          descriptionSuggestion: metadata.descriptionSuggestion,
                                          suggestedDomainIds: metadata.suggestedDomainIds || [],
                                          domainConfidence: metadata.domainConfidence || {},
                                          overallConfidence: metadata.overallConfidence || 0,
                                          domainReasonings: metadata.domainReasonings || []
                                        },
                                        uploadedFiles: uploadedFileData,
                                        sourceMethod: 'file-import',
                                        selectedChannelId,
                                        selectedChannelName: selectedChannel?.name || null
                                      }
                                    });
                                    setIsUploading(false);
                                    return; // Exit early, don't continue with old flow
                                  }
                                } catch (aiError) {
                                  console.error('Error calling AI function:', aiError);
                                }
                              }

                              // Fallback to AI review page with minimal data if AI generation failed
                              if (!title) {
                                const firstFileName = selectedFiles[0].name.split('.')[0].replace(/[_-]/g, ' ');
                                title = `KB: ${firstFileName}`;
                                description = selectedFiles.length === 1
                                  ? `Imported content from: ${selectedFiles[0].name}`
                                  : `Imported content from: ${selectedFiles.map(f => f.name).join(', ')}`;
                              }

                              toast({
                                title: "Files imported successfully",
                                description: `${selectedFiles.length} file(s) have been imported.`,
                              });

                              const selectedChannel = channels.find(c => c.id === selectedChannelId);

                              // Navigate to review page with fallback AI suggestions and channel context
                              navigate("/knowledge-base-review", {
                                state: {
                                  aiSuggestions: {
                                    title: title,
                                    titleSuggestion: "Generated from filename - please refine",
                                    description: description,
                                    descriptionSuggestion: "Please provide a detailed description",
                                    suggestedDomainIds: [],
                                    domainConfidence: {},
                                    overallConfidence: 0.3,
                                    domainReasonings: []
                                  },
                                  uploadedFiles: uploadedFileData,
                                  sourceMethod: 'file-import',
                                  extractedContent: extractedContent,
                                  selectedChannelId,
                                  selectedChannelName: selectedChannel?.name || null
                                }
                              });
                            } catch (error) {
                              console.error('Upload error:', error);
                              toast({
                                title: "Upload Failed",
                                description: "Failed to upload files. Please try again.",
                                variant: "destructive"
                              });
                            } finally {
                              setIsUploading(false);
                            }
                          }}
                          disabled={isUploading || selectedFiles.length === 0}
                        >
                          {isUploading ? "Uploading..." : "Import and Continue"}
                        </Button>
                      </div>
                    </div> : createMethod === "giblet" && <div className="space-y-6">
                      <h3 className="text-xl font-sans text-center mb-6">Ask Giblet to Create Your Knowledge Base</h3>
                      
                      <div className="bg-slate-50 rounded-lg p-4 mb-4">
                        <h4 className="text-lg font-medium mb-4">Select Channel</h4>
                        <ChannelSelector
                          channels={channels}
                          value={selectedChannelId || ''}
                          onValueChange={setSelectedChannelId}
                          placeholder="Choose a channel for this knowledge base"
                          className="w-full"
                        />
                        <p className="text-sm text-gray-500 mt-2">
                          This determines which domain categories will be available for tagging
                        </p>
                      </div>
                      
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-start">
                          <img src="/lovable-uploads/3da8dedf-9a62-4ffb-8d26-4fd50ad51404.png" alt="Giblet" className="h-12 w-12 mr-3" />
                          <div className="flex-1">
                            <p className="text-sm text-gray-600 mb-2">
                              I'm Giblet! Tell me what you'd like to include in your knowledge base, and I'll help structure it.
                            </p>
                            <div className="p-3 bg-white border rounded-lg">
                              <Textarea 
                                value={gibletDescription}
                                onChange={(e) => setGibletDescription(e.target.value)}
                                placeholder="Example: Create a knowledge base about sustainable construction methods covering materials, techniques, and case studies" 
                                className="min-h-24 border-0 focus-visible:ring-0 p-0" 
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <HoverCard>
                        <HoverCardTrigger asChild>
                          <Button variant="ghost" className="w-full text-blue-600 hover:text-blue-800 bg-blue-50 border border-blue-200 hover:bg-blue-100 h-auto py-2">
                            View Sample Prompts
                          </Button>
                        </HoverCardTrigger>
                        <HoverCardContent className="w-80 p-4 bg-white border border-blue-200">
                          <h5 className="font-medium text-blue-800 mb-2">Try These Prompts</h5>
                          <ul className="text-sm text-blue-700 list-disc ml-4 mt-1 space-y-1">
                            <li>Initiate a KB focused on (tbd)</li>
                            <li>Start a Sprint with (names) invited</li>
                            <li>Build an Industry Template for the (tbd) industry</li>
                          </ul>
                        </HoverCardContent>
                      </HoverCard>
                      
                      <div className="flex justify-between pt-4">
                  <Button type="button" variant="active" onClick={resetDemo}>
                    Start Over
                  </Button>
                        <Button 
                          onClick={async () => {
                            if (!selectedChannelId) {
                              toast({
                                title: "Channel Required",
                                description: "Please select a channel for this knowledge base",
                                variant: "destructive"
                              });
                              return;
                            }
                            
                            if (!gibletDescription.trim() || gibletDescription.trim().length < 20) {
                              toast({
                                title: "Description Required",
                                description: "Please provide at least 20 characters describing what you want to create",
                                variant: "destructive"
                              });
                              return;
                            }
                            
                            setIsUploading(true);
                            
                            try {
                              toast({
                                title: "Giblet is thinking...",
                                description: "Analyzing your request to generate metadata"
                              });
                              
                              const { data: metadata, error: aiError } = await supabase.functions.invoke('generate-kb-metadata', {
                                body: { 
                                  content: gibletDescription,
                                  contentType: 'giblet-creation',
                                  channelId: selectedChannelId
                                }
                              });
                              
                              if (aiError) throw aiError;
                              
                              const selectedChannel = channels.find(c => c.id === selectedChannelId);
                              
                              navigate("/knowledge-base-review", {
                                state: {
                                  aiSuggestions: {
                                    title: metadata.title || "",
                                    titleSuggestion: metadata.titleSuggestion || "Generated by Giblet AI",
                                    description: metadata.description || gibletDescription,
                                    descriptionSuggestion: metadata.descriptionSuggestion || "Refined by Giblet",
                                    suggestedDomainIds: metadata.suggestedDomainIds || [],
                                    domainConfidence: metadata.domainConfidence || {},
                                    overallConfidence: metadata.overallConfidence || 0.8,
                                    domainReasonings: metadata.domainReasonings || []
                                  },
                                  sourceMethod: 'giblet',
                                  gibletInput: gibletDescription,
                                  selectedChannelId,
                                  selectedChannelName: selectedChannel?.name || null
                                }
                              });
                            } catch (error) {
                              console.error('Giblet generation error:', error);
                              toast({
                                title: "Generation Failed",
                                description: "Failed to generate metadata. Please try again.",
                                variant: "destructive"
                              });
                            } finally {
                              setIsUploading(false);
                            }
                          }}
                          disabled={isUploading}
                        >
                          {isUploading ? "Generating..." : "Create with Giblet"}
                        </Button>
                      </div>
                    </div>}
                </CardContent>
              </Card>
          </div>
        </div>
      </main>
    </div>;
};

export default Creators;
